import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ClipboardList } from "lucide-react";

interface Props {
  onNavigate: (view: string) => void;
}

export function AssignedPostsWidget({ onNavigate }: Props) {
  const { data: assigned, isLoading } = useQuery({
    queryKey: ["my-assigned-posts"],
    queryFn: async () => {
      const { data } = await (supabase
        .from("my_assigned_posts" as any)
        .select("*")
        .order("assigned_at", { ascending: false })
        .limit(5) as any);
      return (data || []) as any[];
    },
    refetchInterval: 30_000,
  });

  if (isLoading || !assigned?.length) return null;

  return (
    <div className="bg-background border border-border rounded-xl p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={14} className="text-[#C8A84B]" />
          <span className="text-[13px] font-medium">Assigned to Me</span>
        </div>
        <button
          onClick={() => onNavigate("posts")}
          className="text-[11px] text-[#006B3C] hover:underline"
        >
          View all →
        </button>
      </div>

      <div className="space-y-2">
        {assigned.map((post: any) => (
          <div
            key={post.id}
            className="flex items-start gap-3 py-2 border-b border-border last:border-0"
          >
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${
                post.platform === "instagram"
                  ? "bg-pink-100 text-pink-700"
                  : post.platform === "linkedin"
                  ? "bg-blue-100 text-blue-700"
                  : post.platform === "twitter"
                  ? "bg-sky-100 text-sky-700"
                  : "bg-[#E1F5EE] text-[#006B3C]"
              }`}
            >
              {post.platform?.toUpperCase().slice(0, 2) ?? "??"}
            </span>

            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] truncate">
                {post.content?.slice(0, 60)}
                {(post.content?.length ?? 0) > 60 ? "…" : ""}
              </p>
              <p className="text-justify text-[11px] text-muted-foreground mt-0.5">
                Assigned by {post.assigned_by_name || "Team"}
                {post.assigned_at
                  ? ` · ${new Date(post.assigned_at).toLocaleDateString()}`
                  : ""}
              </p>
            </div>

            <span
              className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${
                post.status === "scheduled"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {post.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
